/**
 * Клавіші платформи як дані (keyboard.md, розд. 1, 2, 5): глобальні клавіші оболонки, клавіші
 * записів і списків, яких модулі не займають, і клавіші браузера й ОС, яких не займає ніхто.
 * Компоненти беруть прив'язки звідси, а тест keymap.test.ts звіряє з цим вебманіфести модулів
 * (module-contract.md, розд. 5.8: «модуль не перевизначає глобальних клавіш»).
 */
import { keys as k } from '../i18n/uk/keys.ts';
import type { Hotkey } from './hotkeys.ts';

type Chord = Pick<Hotkey, 'code' | 'mod' | 'shift' | 'alt' | 'char' | 'prefix' | 'inInput'>;

/** Глобальні клавіші оболонки: значать одне на кожному екрані. */
export const GLOBAL = {
  palette: { code: 'KeyK', mod: true, inInput: true },
  search: { code: 'Slash' },
  cheatsheet: { code: 'Slash', shift: true, char: '?' },
  goPrefix: { code: 'KeyG' },
  goHome: { prefix: 'KeyG', code: 'KeyH' },
  goList: { prefix: 'KeyG', code: 'KeyD' },
  goToday: { prefix: 'KeyG', code: 'KeyT' },
  /** Центр сповіщень (NTF-13): відкрити панель, фокус — на перший пункт. */
  goNotifications: { prefix: 'KeyG', code: 'KeyN' },
  today: { code: 'KeyT' },
  toasts: { code: 'F8', inInput: true },
  /** Поза карткою — швидке створення (DAT-01); у картці `N` — нова нотатка (PLATFORM_RECORD_KEYS). */
  quickCreate: { code: 'KeyN' },
  /** Скасувати останню дію поза полями (UX-14, UX-15): поки що — видалення в кошик. */
  undoLast: { code: 'KeyZ', mod: true },
} as const satisfies Record<string, Chord>;

/**
 * Глобальні клавіші, ще не реалізовані, але вже закріплені за платформою (keyboard.md, розд. 1):
 * модуль не може їх зайняти й тоді, коли їх немає в коді.
 */
export const GLOBAL_PLANNED: readonly Chord[] = [{ prefix: 'KeyG', code: 'KeyP' }];

/** Клавіші записів і списків платформи (module-contract.md, розд. 5.8): модулям не можна. */
export const PLATFORM_RECORD_KEYS: readonly string[] = [
  'KeyC',
  'KeyL',
  'KeyN',
  'BracketLeft',
  'BracketRight',
  'KeyF',
  'KeyJ',
  'KeyK',
  'KeyX',
  'KeyS',
  'Space',
  ...Array.from({ length: 10 }, (_, i) => `Digit${i}`),
];

/** Клавіші браузера й ОС (keyboard.md, правило 6): їх не займає ніхто. */
export const BROWSER_RESERVED: readonly Chord[] = [
  ...['KeyN', 'KeyT', 'KeyW', 'KeyL', 'KeyR', 'KeyF', 'KeyP', 'Tab'].map((code) => ({
    code,
    mod: true,
  })),
  { code: 'KeyT', mod: true, shift: true },
  ...['F5', 'F6', 'F11'].map((code) => ({ code })),
  { code: 'ArrowLeft', alt: true },
  { code: 'ArrowRight', alt: true },
];

/** Однакова прив'язка: та сама клавіша, модифікатори й префікс. */
export const sameChord = (a: Chord, b: Chord) =>
  a.code === b.code &&
  Boolean(a.mod) === Boolean(b.mod) &&
  Boolean(a.shift) === Boolean(b.shift) &&
  Boolean(a.alt) === Boolean(b.alt) &&
  (a.prefix ?? '') === (b.prefix ?? '');

/** Підписи для шпаргалки: клавіші, які компонент обробляє сам у `onKeyDown` (без `handler`). */
const hints = (label: string, ...codes: string[]): Hotkey[] =>
  codes.map((code) => ({ code, label }));

/** Таблиця списку (features/list/RecordTable.tsx; keyboard.md, розд. 2). */
export const LIST_KEY_HINTS: readonly Hotkey[] = [
  ...hints(k.keyRowNext, 'KeyJ', 'KeyK', 'ArrowDown', 'ArrowUp'),
  ...hints(k.keyRowEdge, 'Home', 'End'),
  ...hints(k.keyRowPage, 'PageUp', 'PageDown'),
  ...hints(k.keyOpen, 'Enter'),
  { code: 'Enter', mod: true, label: k.keyOpenNewTab },
  ...hints(k.keyRowSelect, 'Space'),
  { code: 'KeyJ', shift: true, label: k.keyRowExtend },
  { code: 'KeyK', shift: true, label: k.keyRowExtend },
  { code: 'KeyA', mod: true, label: k.keySelectAllLoaded },
  ...hints(k.keyCall, 'KeyC'),
  ...hints(k.keyLog, 'KeyL'),
  ...hints(k.keyClearSelection, 'Escape'),
];

/** Панель «Сьогодні» (features/today/TodayPanel.tsx). */
export const TODAY_KEY_HINTS: readonly Hotkey[] = [
  ...hints(k.keyTaskNext, 'KeyJ', 'KeyK', 'ArrowDown', 'ArrowUp'),
  ...hints(k.keyTaskDone, 'KeyX'),
  { code: 'KeyX', shift: true, label: k.keyTaskDoneOutcome },
  ...hints(k.keyTaskSnooze, 'KeyS'),
  ...hints(k.keyCall, 'KeyC'),
  ...hints(k.keyOpen, 'Enter'),
];
